import {useEffect, useRef} from 'react'
import {createPortal} from 'react-dom'
import {MdWarning, MdError, MdInfo} from 'react-icons/md'

interface ConfirmationModalProps {
    isOpen: boolean
    title: string
    message: string
    confirmText?: string
    cancelText?: string
    onConfirm: () => void
    onCancel: () => void
    variant?: 'danger' | 'warning' | 'info'
    isLoading?: boolean
}

export function ConfirmationModal({
                                      isOpen,
                                      title,
                                      message,
                                      confirmText = 'Confirm',
                                      cancelText = 'Cancel',
                                      onConfirm,
                                      onCancel,
                                      variant = 'warning',
                                      isLoading = false
                                  }: ConfirmationModalProps) {
    const modalRef = useRef<HTMLDivElement>(null)

    // Handle escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen && !isLoading) {
                onCancel()
            }
        }

        if (isOpen) {
            document.addEventListener('keydown', handleEscape)
            // Focus the modal for accessibility
            modalRef.current?.focus()
        }

        return () => {
            document.removeEventListener('keydown', handleEscape)
        }
    }, [isOpen, onCancel, isLoading])

    if (!isOpen) return null

    const getIcon = () => {
        switch (variant) {
            case 'danger':
                return <MdError className="text-3xl" style={{ color: '#7728ff' }}/>
            case 'warning':
                return <MdWarning className="text-3xl text-yellow-500"/>
            case 'info':
                return <MdInfo className="text-3xl text-blue-500"/>
        }
    }

    const getButtonStyle = () => {
        switch (variant) {
            case 'danger':
                return 'hover:opacity-90 focus:ring-2 focus:ring-offset-2'
            case 'warning':
                return 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500'
            case 'info':
                return 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
        }
    }

    const getButtonBgColor = () => {
        if (variant === 'danger') {
            return { backgroundColor: '#7728ff' }
        }
        return {}
    }

    const handleConfirm = () => {
        if (!isLoading) {
            onConfirm()
        }
    }

    const modal = (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
        >
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
                onClick={!isLoading ? onCancel : undefined}
            />

            {/* Modal */}
            <div
                ref={modalRef}
                className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 transform transition-all"
                tabIndex={-1}
            >
                <div className="p-6">
                    {/* Icon and Title */}
                    <div className="flex items-center space-x-3 mb-4">
                        {getIcon()}
                        <h3 id="modal-title" className="text-lg font-semibold text-gray-900">
                            {title}
                        </h3>
                    </div>

                    {/* Message */}
                    <p className="text-gray-600 ml-10">
                        {message}
                    </p>
                </div>

                {/* Actions */}
                <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 rounded-b-lg">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={isLoading}
                        className={`px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 ${
                            isLoading ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                    >
                        {cancelText}
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={isLoading}
                        style={getButtonBgColor()}
                        className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none ${getButtonStyle()} ${
                            isLoading ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                    >
                        {isLoading ? (
                            <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg"
                     fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </span>
                        ) : (
                            confirmText
                        )}
                    </button>
                </div>
            </div>
        </div>
    )

    // Use portal to render at document root
    return createPortal(modal, document.body)
}
